import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Switch,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  OutlinedInput,
  Button,
  TextField,
} from "@mui/material";
import { C } from "../constants";
import { FIELD_TONES } from "./FormControls";
import { FONT } from "../theme/tokens";
import { uploadFiles, addSubject } from "../api";

export function normalizeSubjectRow(raw, index = 0) {
  if (!raw) return null;
  if (typeof raw === "string") {
    const m = raw.match(/^([A-Z0-9]{5,12})\s*[—–-]\s*(.+)$/i);
    if (m) return { id: m[1].toUpperCase(), code: m[1].toUpperCase(), name: m[2].trim() };
    const name = raw.trim();
    if (name.length < 3) return null;
    return { id: `sub-${index}-${name.slice(0, 24)}`, code: "", name };
  }
  const code = String(raw.code || "").trim().toUpperCase();
  const name = String(raw.name || raw.title || "").trim();
  if (!name && !code) return null;
  return { id: code || `sub-${index}-${name.slice(0, 24)}`, code, name: name || code };
}

export function parseSubjectsFromSyllabusText(text = "") {
  const lines = String(text || "").split(/\r?\n/);
  const out = [];
  const seen = new Set();
  const push = (row) => {
    if (!row) return;
    const key = subjectPrefKey(row);
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(row);
  };
  let inBlock = false;
  for (const line of lines) {
    if (/^\s*SUBJECTS\s*:/i.test(line)) { inBlock = true; continue; }
    if (inBlock && /^\s*[A-Z_][A-Z0-9_]*\s*:/.test(line) && !/^\s*-/.test(line) && !/^\s*SUBJECTS/i.test(line)) {
      inBlock = false;
    }
    const cleaned = line.replace(/^\s*[-•*]\s*/, "").replace(/^\s*\d+[.)]\s*/, "").trim();
    if (!cleaned) continue;
    const row = normalizeSubjectRow(cleaned, out.length);
    if (inBlock) {
      if (row) push(row);
      continue;
    }
    // Campus rows like 23CS301 — Data Structures
    if (/^[A-Z]{2,}\d{2,}[A-Z0-9]*\s*[—–-]/.test(cleaned) && row) push(row);
  }
  return out;
}

export function subjectPrefKey(s) {
  return String(s?.code || s?.id || s?.name || "").toLowerCase().trim();
}

export function mergeSubjectPreferences(catalog = [], existing = []) {
  const prev = new Map((existing || []).map((p) => [subjectPrefKey(p), p]));
  const out = [];
  const seen = new Set();
  for (let i = 0; i < (catalog || []).length; i++) {
    const row = normalizeSubjectRow(catalog[i], i);
    if (!row) continue;
    const key = subjectPrefKey(row);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const src = catalog[i] && typeof catalog[i] === "object" ? catalog[i] : {};
    const old = prev.get(key);
    out.push({
      id: row.id,
      code: row.code || old?.code || "",
      name: row.name || old?.name || "",
      included: old?.included !== false,
      sentiment: ["like", "dislike", "neutral"].includes(old?.sentiment) ? old.sentiment : "neutral",
      mustLearnConcepts: Array.isArray(old?.mustLearnConcepts) ? old.mustLearnConcepts : [],
      omitConcepts: Array.isArray(old?.omitConcepts) ? old.omitConcepts : [],
      syllabusText: String(old?.syllabusText || src.syllabusText || "").trim(),
      docNames: Array.isArray(old?.docNames)
        ? old.docNames
        : (Array.isArray(src.docNames) ? src.docNames : []),
    });
  }
  for (const old of existing || []) {
    const key = subjectPrefKey(old);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push({
      ...old,
      included: old.included !== false,
      sentiment: ["like", "dislike", "neutral"].includes(old.sentiment) ? old.sentiment : "neutral",
      mustLearnConcepts: Array.isArray(old.mustLearnConcepts) ? old.mustLearnConcepts : [],
      omitConcepts: Array.isArray(old.omitConcepts) ? old.omitConcepts : [],
      syllabusText: String(old.syllabusText || "").trim(),
      docNames: Array.isArray(old.docNames) ? old.docNames : [],
    });
  }
  return out;
}

export function summarizeSubjectPreferences(prefs = []) {
  const list = Array.isArray(prefs) ? prefs : [];
  const included = list.filter((p) => p.included !== false);
  return {
    total: list.length,
    included: included.length,
    liked: included.filter((p) => p.sentiment === "like").map((p) => p.name),
    disliked: included.filter((p) => p.sentiment === "dislike").map((p) => p.name),
    excluded: list.filter((p) => p.included === false).map((p) => p.name),
  };
}

const FOCUS = FIELD_TONES.amber;
const LIGHT = FIELD_TONES.coral;
const TEAL = FIELD_TONES.teal;
const BLUE = FIELD_TONES.blue;

export default function SubjectPreferencesPanel({
  preferences = [],
  onChange,
  title = "Subject preferences",
  dense = false,
  emptyHint = "Load campus syllabus (Basics → college / dept / sem) or paste subjects first.",
}) {
  const summary = useMemo(() => summarizeSubjectPreferences(preferences), [preferences]);
  const [query, setQuery] = useState("");
  const [addInput, setAddInput] = useState("");
  const [conceptEditorKey, setConceptEditorKey] = useState(null);
  const [conceptNeedsText, setConceptNeedsText] = useState("");
  const [conceptOmitText, setConceptOmitText] = useState("");
  const [uploadingKey, setUploadingKey] = useState(null);
  const [uploadMsg, setUploadMsg] = useState(null);

  const parseConceptText = (txt) => {
    const s = String(txt || "");
    const parts = s.split(/[\n,]+/g).map((x) => x.trim()).filter(Boolean);
    const seen = new Set();
    const out = [];
    for (const p of parts) {
      const k = p.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(p);
    }
    return out;
  };

  const updateOne = (key, patch) => {
    onChange?.((preferences || []).map((p) => (subjectPrefKey(p) === key ? { ...p, ...patch } : p)));
  };

  const setAllIncluded = (val) => {
    onChange?.((preferences || []).map((p) => ({ ...p, included: val })));
  };

  const handleAdd = () => {
    const name = addInput.trim();
    if (!name || name.length < 2) return;
    const existing = (preferences || []).map((p) => subjectPrefKey(p));
    const newRow = normalizeSubjectRow(name, preferences.length);
    if (!newRow) return;
    if (existing.includes(subjectPrefKey(newRow))) {
      setAddInput("");
      return;
    }
    onChange?.([
      ...(preferences || []),
      {
        ...newRow,
        included: true,
        sentiment: "neutral",
        mustLearnConcepts: [],
        omitConcepts: [],
        syllabusText: "",
        docNames: [],
      },
    ]);
    setAddInput("");
    addSubject(newRow.name, "").catch(() => {});
  };

  const persistSubjectDocs = (name, syllabusText) => {
    if (!name) return;
    addSubject(name, syllabusText || "").catch(() => {});
  };

  const handleUploadDocs = async (pref, fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    const key = subjectPrefKey(pref);
    setUploadingKey(key);
    setUploadMsg(null);
    try {
      const res = await uploadFiles({ syllabus: files });
      const chunks = (res.extracted?.syllabus || []).map(
        (f) => `\n[${f.filename}]\n${f.content || ""}`
      );
      const extracted = chunks.join("\n").trim();
      const names = files.map((f) => f.name);
      const nextText = [pref.syllabusText, extracted].filter(Boolean).join("\n\n");
      const nextNames = Array.from(new Set([...(pref.docNames || []), ...names]));
      updateOne(key, { syllabusText: nextText, docNames: nextNames });
      persistSubjectDocs(pref.name, nextText);
      setUploadMsg({ type: "ok", text: `Added ${names.length} file(s) to ${pref.name}` });
    } catch (err) {
      setUploadMsg({
        type: "error",
        text: err.response?.data?.error || err.message || "Upload failed",
      });
    }
    setUploadingKey(null);
  };

  const clearDocs = (pref) => {
    const key = subjectPrefKey(pref);
    updateOne(key, { syllabusText: "", docNames: [] });
    persistSubjectDocs(pref.name, "");
  };

  const removeOne = (key) => {
    onChange?.((preferences || []).filter((p) => subjectPrefKey(p) !== key));
  };

  const shellSx = {
    borderRadius: "10px",
    border: `1px solid ${C.border}`,
    background: C.surface,
    overflow: "hidden",
    boxShadow: "none",
  };

  if (!preferences?.length) {
    return (
      <Box sx={shellSx}>
        <Box
          sx={{
            px: 2,
            py: 1.5,
            background: "#F8FAFC",
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                bgcolor: "#fff",
                border: `1px solid ${TEAL.accent}44`,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: TEAL.accent,
              }}
            >
              <i className="ti ti-books" style={{ fontSize: 16 }} />
            </Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.text }}>{title}</Typography>
          </Stack>
        </Box>
        <Box sx={{ p: 2.25 }}>
          <Typography sx={{ color: C.muted, fontSize: 12.5, lineHeight: 1.5, textAlign: "center", mb: 1.75 }}>
            {emptyHint}
            {" "}Add a subject name here, then upload its syllabus or notes (PDF, DOCX, TXT).
          </Typography>
          <Stack direction="row" spacing={1}>
            <OutlinedInput
              size="small"
              value={addInput}
              onChange={(e) => setAddInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              placeholder="Type subject name and press Enter…"
              fullWidth
              sx={{
                fontSize: 13,
                borderRadius: "8px",
                height: 40,
                bgcolor: "#fff",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: C.borderStrong, borderWidth: 1 },
              }}
            />
            <Button
              size="small"
              variant="contained"
              disableElevation
              onClick={handleAdd}
              disabled={addInput.trim().length < 2}
              sx={{
                fontSize: 12,
                fontWeight: 700,
                minWidth: 64,
                borderRadius: 2.5,
                textTransform: "none",
                bgcolor: TEAL.accent,
                "&:hover": { bgcolor: TEAL.chip },
              }}
            >
              Add
            </Button>
          </Stack>
        </Box>
      </Box>
    );
  }

  const q = String(query || "").trim().toLowerCase();
  const filtered = (preferences || []).filter((p) => {
    if (!q) return true;
    return String(p?.name || "").toLowerCase().includes(q) || String(p?.code || "").toLowerCase().includes(q);
  });

  return (
    <Box sx={shellSx}>
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          background: "#F8FAFC",
          borderBottom: `1px solid ${C.border}`,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                bgcolor: "#fff",
                border: `1px solid ${TEAL.accent}44`,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: TEAL.accent,
              }}
            >
              <i className="ti ti-books" style={{ fontSize: 16 }} />
            </Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.text, letterSpacing: -0.2 }}>
              {title}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.75}>
            <Chip
              size="small"
              label="All on"
              onClick={() => setAllIncluded(true)}
              sx={{
                fontSize: 11,
                fontWeight: 700,
                height: 26,
                cursor: "pointer",
                bgcolor: C.successBg,
                color: C.success,
                border: `1px solid ${C.success}44`,
                "&:hover": { bgcolor: "#D1FAE5" },
              }}
            />
            <Chip
              size="small"
              label="All off"
              onClick={() => setAllIncluded(false)}
              sx={{
                fontSize: 11,
                fontWeight: 700,
                height: 26,
                cursor: "pointer",
                bgcolor: C.errorBg,
                color: C.error,
                border: `1px solid ${C.error}33`,
                "&:hover": { bgcolor: "#FECACA" },
              }}
            />
          </Stack>
        </Stack>

        <Stack direction="row" spacing={0.75} sx={{ mt: 1.25 }} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            icon={<i className="ti ti-check" style={{ fontSize: 12, color: BLUE.chip }} />}
            label={`${summary.included} of ${summary.total} included`}
            sx={{
              fontSize: 11,
              height: 24,
              bgcolor: summary.included ? BLUE.soft : C.errorBg,
              color: summary.included ? BLUE.chip : C.error,
              fontWeight: 700,
              border: `1px solid ${summary.included ? BLUE.accent : C.error}33`,
            }}
          />
          {summary.liked.length > 0 && (
            <Chip
              size="small"
              icon={<i className="ti ti-star-filled" style={{ fontSize: 11, color: FOCUS.chip }} />}
              label={`${summary.liked.length} focus`}
              sx={{
                fontSize: 11,
                height: 24,
                bgcolor: FOCUS.soft,
                color: FOCUS.chip,
                fontWeight: 700,
                border: `1px solid ${FOCUS.accent}44`,
              }}
            />
          )}
          {summary.disliked.length > 0 && (
            <Chip
              size="small"
              icon={<i className="ti ti-arrow-down" style={{ fontSize: 11, color: LIGHT.chip }} />}
              label={`${summary.disliked.length} light`}
              sx={{
                fontSize: 11,
                height: 24,
                bgcolor: LIGHT.soft,
                color: LIGHT.chip,
                fontWeight: 700,
                border: `1px solid ${LIGHT.accent}44`,
              }}
            />
          )}
        </Stack>

        <Box
          sx={{
            mt: 1.35,
            px: 1.5,
            py: 1.1,
            borderRadius: 2,
            background: "#F8FAFC",
            border: `1px solid ${C.border}`,
            boxShadow: "none",
          }}
        >
          <Typography sx={{ fontSize: 12, color: C.muted, lineHeight: 1.45, fontWeight: 400 }}>
            Included subjects drive Learning. Add a name, then upload that subject&apos;s docs.
            <strong> Focus</strong> = deeper practice · <strong>Light</strong> = lighter coverage.
            Concepts are optional — only when you care.
          </Typography>
        </Box>

        <Stack direction="row" spacing={2} sx={{ mt: 1.1 }} flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>
            <i className="ti ti-star-filled" style={{ fontSize: 11, color: FOCUS.accent, marginRight: 4 }} />
            Focus = more depth
          </Typography>
          <Typography sx={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>
            <i className="ti ti-feather" style={{ fontSize: 11, color: LIGHT.accent, marginRight: 4 }} />
            Light = basics only
          </Typography>
          <Typography sx={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>
            <i className="ti ti-equal" style={{ fontSize: 11, color: C.muted, marginRight: 4 }} />
            Normal = standard
          </Typography>
        </Stack>
      </Box>

      {preferences.length > 5 && (
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <OutlinedInput
            size="small"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search subjects…"
            fullWidth
            startAdornment={
              <InputAdornment position="start">
                <i className="ti ti-search" style={{ fontSize: 15, color: TEAL.accent }} />
              </InputAdornment>
            }
            sx={{
              fontSize: 13,
              borderRadius: 2.5,
              height: 38,
              bgcolor: TEAL.soft + "55",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: `${TEAL.accent}40`, borderWidth: 1.5 },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: TEAL.accent },
            }}
          />
        </Box>
      )}

      {!summary.included && (
        <Box
          sx={{
            mx: 2,
            mb: 1,
            mt: preferences.length > 5 ? 0 : 1.25,
            px: 1.5,
            py: 1.1,
            borderRadius: 2,
            bgcolor: FOCUS.soft,
            border: `1.5px solid ${FOCUS.accent}55`,
          }}
        >
          <Typography sx={{ fontSize: 12, color: FOCUS.chip, fontWeight: 700 }}>
            All subjects are off — the plan won&apos;t include any academic topics.
          </Typography>
        </Box>
      )}

      {uploadMsg && (
        <Box
          sx={{
            mx: 2,
            mt: 1.25,
            px: 1.5,
            py: 1,
            borderRadius: 2,
            bgcolor: uploadMsg.type === "error" ? C.errorBg : C.successBg,
            border: `1px solid ${uploadMsg.type === "error" ? C.error : C.success}44`,
          }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: uploadMsg.type === "error" ? C.error : C.success }}>
            {uploadMsg.text}
          </Typography>
        </Box>
      )}

      <Box sx={{ maxHeight: dense ? 280 : 420, overflowY: "auto", px: 1.25, py: 1, bgcolor: "#F8FAFC" }}>
        {filtered.map((p) => {
          const key = subjectPrefKey(p);
          const on = p.included !== false;
          const sentiment = p.sentiment || "neutral";
          const isLiked = sentiment === "like";
          const isDisliked = sentiment === "dislike";
          const isCustom = Boolean(p.id && String(p.id).startsWith("sub-"));
          const needsArr = Array.isArray(p.mustLearnConcepts) ? p.mustLearnConcepts : [];
          const omitArr = Array.isArray(p.omitConcepts) ? p.omitConcepts : [];
          const accent = !on ? C.border : isLiked ? FOCUS.accent : isDisliked ? LIGHT.accent : TEAL.accent;
          const soft = !on ? C.surfaceAlt : isLiked ? FOCUS.soft : isDisliked ? LIGHT.soft : "#fff";

          return (
            <Box key={key} sx={{ mb: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: dense ? 0.85 : 1.1,
                  borderRadius: 2.5,
                  border: `1.5px solid ${accent}40`,
                  bgcolor: soft,
                  boxShadow: on ? `inset 3px 0 0 ${accent}, 0 4px 12px ${accent}10` : "none",
                  opacity: on ? 1 : 0.55,
                  transition: "opacity 0.15s, background 0.15s, border-color 0.15s",
                  "&:hover": { borderColor: `${accent}70` },
                }}
              >
                <Switch
                  size="small"
                  checked={on}
                  onChange={(e) => updateOne(key, { included: e.target.checked })}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: TEAL.accent },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: TEAL.accent },
                  }}
                />
                <Chip
                  size="small"
                  label={on ? "In schedule" : "Skipped"}
                  sx={{
                    height: 22,
                    fontSize: 10.5,
                    fontWeight: 800,
                    bgcolor: on ? C.successBg : C.errorBg,
                    color: on ? C.success : C.error,
                    border: `1px solid ${on ? C.success : C.error}44`,
                  }}
                />

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: on ? C.text : C.muted,
                      lineHeight: 1.35,
                      textDecoration: on ? "none" : "line-through",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {p.name}
                    {isLiked && (
                      <i
                        className="ti ti-star-filled"
                        style={{ fontSize: 12, color: FOCUS.accent, marginLeft: 6, verticalAlign: "middle" }}
                      />
                    )}
                  </Typography>
                  {p.code && (
                    <Typography
                      sx={{
                        fontSize: 10.5,
                        color: TEAL.chip,
                        mt: 0.2,
                        fontFamily: FONT.mono,
                        letterSpacing: 0.4,
                        fontWeight: 650,
                      }}
                    >
                      {p.code}
                    </Typography>
                  )}
                  {(p.docNames || []).length > 0 && (
                    <Typography sx={{ fontSize: 10.5, color: TEAL.chip, mt: 0.35, fontWeight: 650 }}>
                      {(p.docNames || []).length} file{(p.docNames || []).length === 1 ? "" : "s"}: {(p.docNames || []).slice(0, 3).join(", ")}
                      {(p.docNames || []).length > 3 ? "…" : ""}
                      {" · "}
                      <Box
                        component="span"
                        onClick={() => clearDocs(p)}
                        sx={{ cursor: "pointer", textDecoration: "underline", color: C.muted }}
                      >
                        clear
                      </Box>
                    </Typography>
                  )}
                </Box>

                {on && (
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={sentiment}
                    onChange={(_, val) => {
                      if (val) updateOne(key, { sentiment: val });
                    }}
                    sx={{
                      height: 28,
                      bgcolor: "#fff",
                      borderRadius: 2,
                      border: `1px solid ${C.border}`,
                      "& .MuiToggleButton-root": {
                        fontSize: 10.5,
                        fontWeight: 700,
                        px: 1.1,
                        py: 0,
                        textTransform: "none",
                        border: "none",
                        color: C.muted,
                        "&.Mui-selected": { border: "none" },
                      },
                    }}
                  >
                    <ToggleButton
                      value="like"
                      sx={{
                        borderRadius: "8px 0 0 8px !important",
                        "&.Mui-selected, &.Mui-selected:hover": {
                          bgcolor: FOCUS.soft,
                          color: FOCUS.chip,
                        },
                        "&:hover": { bgcolor: FOCUS.soft },
                      }}
                    >
                      ★ Focus
                    </ToggleButton>
                    <ToggleButton
                      value="neutral"
                      sx={{
                        "&.Mui-selected, &.Mui-selected:hover": {
                          bgcolor: BLUE.soft,
                          color: BLUE.chip,
                        },
                      }}
                    >
                      Normal
                    </ToggleButton>
                    <ToggleButton
                      value="dislike"
                      sx={{
                        borderRadius: "0 8px 8px 0 !important",
                        "&.Mui-selected, &.Mui-selected:hover": {
                          bgcolor: LIGHT.soft,
                          color: LIGHT.chip,
                        },
                        "&:hover": { bgcolor: LIGHT.soft },
                      }}
                    >
                      Light
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}

                {on && (
                  <Button
                    size="small"
                    component="label"
                    variant="outlined"
                    disabled={uploadingKey === key}
                    sx={{
                      minWidth: 0,
                      px: 1,
                      fontSize: 11,
                      fontWeight: 700,
                      color: TEAL.chip,
                      borderColor: `${TEAL.accent}44`,
                      borderRadius: 2,
                      textTransform: "none",
                      bgcolor: "#fff",
                      "&:hover": { borderColor: TEAL.accent, bgcolor: TEAL.soft },
                    }}
                  >
                    {uploadingKey === key
                      ? "Reading…"
                      : (p.docNames || []).length
                        ? "Add docs"
                        : "Upload docs"}
                    <input
                      type="file"
                      hidden
                      multiple
                      accept=".pdf,.txt,.md,.doc,.docx,.csv,.xlsx,.xls"
                      onChange={(e) => {
                        handleUploadDocs(p, e.target.files);
                        e.target.value = "";
                      }}
                    />
                  </Button>
                )}

                {on && (
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{
                      minWidth: 0,
                      ml: 0.5,
                      px: 1,
                      fontSize: 11,
                      fontWeight: 700,
                      color: BLUE.chip,
                      borderColor: `${BLUE.accent}44`,
                      borderRadius: 2,
                      textTransform: "none",
                      bgcolor: BLUE.soft,
                      "&:hover": { borderColor: BLUE.accent, bgcolor: BLUE.soft },
                    }}
                    onClick={() => {
                      setConceptEditorKey(key);
                      setConceptNeedsText(needsArr.join(", "));
                      setConceptOmitText(omitArr.join(", "));
                    }}
                  >
                    Concepts
                  </Button>
                )}

                {isCustom && (
                  <Box
                    onClick={() => removeOne(key)}
                    sx={{
                      ml: 0.25,
                      cursor: "pointer",
                      color: "#CBD5E1",
                      "&:hover": { color: C.error },
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <i className="ti ti-x" style={{ fontSize: 14 }} />
                  </Box>
                )}
              </Box>

              {conceptEditorKey === key && on && (
                <Box
                  sx={{
                    mt: 0.75,
                    px: 1.75,
                    py: 1.5,
                    borderRadius: 2.5,
                    border: `1.5px solid ${BLUE.accent}33`,
                    background: `linear-gradient(180deg, ${BLUE.soft} 0%, #fff 70%)`,
                    boxShadow: `inset 3px 0 0 ${BLUE.accent}`,
                  }}
                >
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: BLUE.chip, mb: 0.35 }}>
                    Concepts for this subject
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: C.muted, mb: 1.1, lineHeight: 1.4 }}>
                    Optional. Tell the plan what to prioritize or skip inside this course only.
                  </Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.25 }}>
                    <TextField
                      size="small"
                      multiline
                      minRows={3}
                      value={conceptNeedsText}
                      onChange={(e) => setConceptNeedsText(e.target.value)}
                      label="Must learn"
                      placeholder="e.g. pointers, recursion"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          bgcolor: C.successBg,
                          "& fieldset": { borderColor: `${C.success}44` },
                        },
                        "& .MuiInputLabel-root": { color: C.success, fontWeight: 700 },
                      }}
                    />
                    <TextField
                      size="small"
                      multiline
                      minRows={3}
                      value={conceptOmitText}
                      onChange={(e) => setConceptOmitText(e.target.value)}
                      label="Omit / skip"
                      placeholder="e.g. assembly, outdated labs"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          bgcolor: C.errorBg,
                          "& fieldset": { borderColor: `${C.error}33` },
                        },
                        "& .MuiInputLabel-root": { color: C.error, fontWeight: 700 },
                      }}
                    />
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1.25 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setConceptEditorKey(null)}
                      sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
                    >
                      Close
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      disableElevation
                      onClick={() => {
                        updateOne(key, {
                          mustLearnConcepts: parseConceptText(conceptNeedsText),
                          omitConcepts: parseConceptText(conceptOmitText),
                        });
                        setConceptEditorKey(null);
                      }}
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 700,
                        bgcolor: TEAL.accent,
                        "&:hover": { bgcolor: TEAL.chip },
                      }}
                    >
                      Save
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          );
        })}

        {!filtered.length && (
          <Box sx={{ py: 3.5, textAlign: "center" }}>
            <Typography sx={{ color: C.muted, fontSize: 12.5, fontWeight: 600 }}>
              No subjects match &quot;{q}&quot;
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: `1px solid ${TEAL.accent}28`,
          background: `linear-gradient(90deg, ${TEAL.soft} 0%, ${C.surfaceAlt} 100%)`,
        }}
      >
        <Stack direction="row" spacing={1}>
          <OutlinedInput
            size="small"
            value={addInput}
            onChange={(e) => setAddInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            placeholder="Add a subject…"
            fullWidth
            startAdornment={
              <InputAdornment position="start">
                <i className="ti ti-plus" style={{ fontSize: 14, color: TEAL.accent }} />
              </InputAdornment>
            }
            sx={{
              fontSize: 13,
              borderRadius: 2.5,
              height: 38,
              bgcolor: "#fff",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: `${TEAL.accent}40`, borderWidth: 1.5 },
            }}
          />
          <Button
            size="small"
            variant="contained"
            onClick={handleAdd}
            disabled={addInput.trim().length < 2}
            disableElevation
            sx={{
              fontSize: 12,
              fontWeight: 700,
              minWidth: 56,
              borderRadius: 2.5,
              textTransform: "none",
              bgcolor: TEAL.accent,
              "&:hover": { bgcolor: TEAL.chip },
            }}
          >
            Add
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
